/*global google*/

import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { OrderGroupContext } from './PlaceOrder';
import { getPlaceLongName, handleGetOrdersByOrderGroup, ordersState, setDisableUpdateOrderGroup, setOrderGroup, setShowSavedPickUpAddrModal } from './placeOrderSlice';
import { toLocation, useAddressAutocomplete } from './addressAutocomplete';
import { createOrderGroup, updateOrderGroup } from './orderGroupAPI';
import { loadGuestDraft } from './guestDraft';

export function OrderGroup(props) {
  const dispatch = useDispatch();
  const orderListState = useSelector(ordersState);
  const orderGroupContext = useContext(OrderGroupContext);

  const { id } = useParams();
  const { handlePickUpLocation, handlePickUpDateTime, handleError, handleErrorMsg } = props;

  const [pickUpForm, setPickUpForm] = useState(() => {
    const draft = id ? null : loadGuestDraft();

    return {
      deliveryMethod: draft ? draft.deliveryMethod : "MOTOR",
      pickUpPersonName: "",
      pickUpContactNumber: "",
      pickUpDateTime: draft ? draft.pickUpDateTime : "",
      pickUpLocation: {
        unitNumber: "",
        address: "",
        coordinate: {
          longitude: "",
          latitude: ""
        },
        city: "",
        country: ""
      },
      halal: draft ? draft.halal : false,
    };
  });
  const [currentLocation, setCurrentLocation] = useState({
    lat: null,
    lng: null
  });
  const [locationEnabled, setLocationEnabled] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    setPickUpForm(prevState => ({
      ...prevState,
      pickUpPersonName: orderGroupContext.pickUpForm.pickUpPersonName,
      pickUpContactNumber: orderGroupContext.pickUpForm.pickUpContactNumber,
      pickUpLocation: {
        ...prevState.pickUpLocation,
        ...orderGroupContext.pickUpForm.pickUpLocation,
      }
    }));

    if (
      orderListState.orderGroup.id !== null && (
        pickUpForm.pickUpPersonName !== orderGroupContext.pickUpForm.pickUpPersonName ||
        pickUpForm.pickUpContactNumber !== orderGroupContext.pickUpForm.pickUpContactNumber ||
        JSON.stringify(pickUpForm.pickUpLocation) !== JSON.stringify(orderGroupContext.pickUpForm.pickUpLocation)
      )
    ) {
      setFormSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderGroupContext.pickUpForm]);

  const handleUseCurrentLocation = () => {
    if (!locationEnabled) {
      handleError(true);
      handleErrorMsg("Please enable your location services to retrieve your current location.");
      return;
    }

    if (currentLocation.lat == null) {
      handleError(true);
      handleErrorMsg("Retrieving your current location... Please try again after 10 seconds.");
      return;
    }

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: currentLocation }, (results, status) => {
      if (status === "OK") {
        if (results[0]) {
          let autocompleteInput = document.getElementById("pickUpAutocomplete");
          autocompleteInput.value = results[0].formatted_address;

          const location = {
            address: `${results[0].formatted_address}`,
            coordinate: {
              latitude: results[0].geometry.location.lat(),
              longitude: results[0].geometry.location.lng(),
            },
            street: getPlaceLongName(results[0].address_components, "street_address"),
            apartment: getPlaceLongName(results[0].address_components, "street_address"),
            state: getPlaceLongName(results[0].address_components, "administrative_area_level_1"),
            postalCode: getPlaceLongName(results[0].address_components, "postal_code"),
            city: getPlaceLongName(results[0].address_components, "locality"),
            country: getPlaceLongName(results[0].address_components, "country")
          };

          setPickUpForm(prevPickUpForm => ({
            ...prevPickUpForm,
            pickUpLocation: {
              ...prevPickUpForm.pickUpLocation,
              ...location
            }
          }));

          handleError(false);
          handleErrorMsg('');

          handlePickUpLocation(location);
        } else {
          handleError(true);
          handleErrorMsg("Error retrieving your current location.");
        }
      } else {
        handleError(true);
        handleErrorMsg("Error retrieving your current location.");
      }
    });
  };

  useAddressAutocomplete("pickUpAutocomplete", (place) => {
    try {
      const location = toLocation(place);

      setPickUpForm(prevPickUpForm => ({
        ...prevPickUpForm,
        pickUpLocation: {
          ...prevPickUpForm.pickUpLocation,
          ...location
        }
      }));

      handlePickUpLocation(location);
      if (orderListState.orderGroup.orders && orderListState.orderGroup.orders.length > 0) {
        setFormSubmitted(false);
      }
    } catch (error) {
      handleError(true);
      handleErrorMsg("Please select a pick-up address from the drop-down list.");
    }
  });

  const validatePickUpDateTime = useCallback((date) => {
    if ((date.getHours() === 0) || (new Date(date) - new Date() < 0)) {
      const currDate = new Date();
      const currHour = currDate.getHours();
      const currMin = currDate.getMinutes();

      const nextBlock = new Date(currDate);
      nextBlock.setSeconds(0, 0);

      if (currMin < 15) {
        nextBlock.setMinutes(15);
      } else if (currMin < 30) {
        nextBlock.setMinutes(30);
      } else if (currMin < 45) {
        nextBlock.setMinutes(45);
      } else {
        nextBlock.setMinutes(0);
        nextBlock.setHours(currHour + 1);
      }

      date.setHours(nextBlock.getHours(), nextBlock.getMinutes(), 0, 0);
    }

    let error = false;
    if (new Date(date) - new Date() < 0) {
      error = true;
      handleError(true);
      handleErrorMsg("Pick-up date should not be in the past.");
      return error;
    }

    if (!error) {
      setPickUpForm((prevPickUpForm) => ({
        ...prevPickUpForm,
        pickUpDateTime: date
      }));
      handlePickUpDateTime(date);
    }
  }, [handlePickUpDateTime, handleError, handleErrorMsg,]);

  const validateFields = useCallback(() => {
    let error = false;

    if (pickUpForm.pickUpLocation.country === null || pickUpForm.pickUpLocation.country === "") {
      error = true;
      handleError(true);
      handleErrorMsg("Please re-select the pick-up address from drop-down list.");
      return error;
    }

    for (var key in pickUpForm) {
      if (key !== "halal" && key !== "noteToDriver") {
        if (pickUpForm[key] === null || pickUpForm[key] === "") {
          error = true;
          handleError(true);
          handleErrorMsg("Please complete all the fields.");
          return error;
        }
      }
    }

    return error;
  }, [pickUpForm, handleError, handleErrorMsg]);

  const submitOrderGroup = useCallback(async () => {
    let error = validateFields();
    if (error) { return; }

    error = validatePickUpDateTime(pickUpForm.pickUpDateTime);

    // validate all delivery dates are later than pick-up dates
    if (new Date(orderGroupContext.dropOffForm.deliverDateTime) - new Date(pickUpForm.pickUpDateTime) < 0) {
      error = true;
      handleError(true);
      handleErrorMsg("Pick-up date should not be later than delivery date(s).");
      return;
    }

    if (orderListState.orderGroup.orders && orderListState.orderGroup.orders.length > 0) {
      orderListState.orderGroup.orders.forEach((order) => {
        if (new Date(order.deliverDateTime) - new Date(pickUpForm.pickUpDateTime) < 0) {
          error = true;
          handleError(true);
          handleErrorMsg("Pick-up date should not be later than delivery date(s).");
          return;
        }
      });
    }

    if (!error) {
      try {
        if (orderListState.orderGroup.id === null) {
          // Create order group
          let orderGroupPayload = {
            ...pickUpForm,
            pickUpDateTime: new Date(pickUpForm.pickUpDateTime).toISOString()
          }

          const response = await createOrderGroup(orderGroupPayload);
          dispatch(setOrderGroup(response.data));
          handleError(false);
          handleErrorMsg('');
          window.location = `/app/order-groups/${response.data.id}`;
        } else if (!orderListState.disableUpdateOrderGroup) {
          dispatch(setDisableUpdateOrderGroup(true));

          // Update order group
          let orderGroupPayload = {
            ...pickUpForm,
            pickUpDateTime: new Date(pickUpForm.pickUpDateTime).toISOString(),
            orders: [
              ...orderListState.orderGroup.orders.map(o => o.deliverDateTime ? { ...o, deliverDateTime: new Date(o.deliverDateTime).toISOString() } : o)
            ]
          }

          await updateOrderGroup(orderGroupPayload, orderListState.orderGroup.id);
          dispatch(setDisableUpdateOrderGroup(false));

          dispatch(handleGetOrdersByOrderGroup(orderListState.orderGroup.id));
          handleError(false);
          handleErrorMsg('');
        }
      } catch (error) {
        handleError(true);
        handleErrorMsg(error.message);
      }
    }
  }, [dispatch, orderListState, pickUpForm, validateFields, validatePickUpDateTime, handleError, handleErrorMsg, orderGroupContext.dropOffForm.deliverDateTime]);

  const onFormValueChange = useCallback((data) => {
    setPickUpForm((prevPickUpForm) => ({
      ...prevPickUpForm,
      [data.name]: data.value
    }));

    if (orderListState.orderGroup.id !== null) {
      setFormSubmitted(false);
    }
  }, [orderListState]);

  useEffect(() => {
    if (id && orderListState.orderGroup.id !== null) {
      setPickUpForm(prevPickUpForm => ({
        ...prevPickUpForm,
        deliveryMethod: orderListState.orderGroup.deliveryMethod,
        pickUpDateTime: orderListState.orderGroup.pickUpDateTime,
        pickUpLocation: orderListState.orderGroup.pickUpLocation,
        halal: orderListState.orderGroup.halal
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, orderListState]);

  useEffect(() => {
    if (
      !formSubmitted
      && pickUpForm.pickUpPersonName !== ""
      && pickUpForm.pickUpContactNumber !== ""
      && pickUpForm.pickUpDateTime !== ""
      && pickUpForm.pickUpLocation.coordinate.longitude !== ""
      && pickUpForm.pickUpLocation.coordinate.latitude !== ""
    ) {
      if (
        orderListState.orderGroup.id !== null && (
          pickUpForm.pickUpPersonName !== orderGroupContext.pickUpForm.pickUpPersonName ||
          pickUpForm.pickUpContactNumber !== orderGroupContext.pickUpForm.pickUpContactNumber ||
          JSON.stringify(pickUpForm.pickUpLocation) !== JSON.stringify(orderGroupContext.pickUpForm.pickUpLocation)
        )
      ) {
        // Make sure pickupForm state is same as orderGroupContext only submit the form
        return;
      }
      setFormSubmitted(true);
      submitOrderGroup();
    }
  }, [orderListState, orderGroupContext, formSubmitted, pickUpForm, submitOrderGroup]);

  useEffect(() => {
    const watchId = navigator.geolocation.watchPosition((position) => {
      setCurrentLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
      setLocationEnabled(true);
    }, () => {
      setLocationEnabled(false);
    });

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);


  return props.children({
    pickUpForm,
    onFormValueChange,
    validatePickUpDateTime,
    setPickUpForm,
    setFormSubmitted,
    handleUseCurrentLocation,
    openSavedAddresses: () => {
      props.closePickUpModal();
      dispatch(setShowSavedPickUpAddrModal(true));
    },
  });
}
